document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-toggle-password]");
  if (!btn) return;

  const fieldset = btn.closest(".password-wrapper");
  const input = fieldset?.querySelector("input.password-field");
  if (!input) return;

  const isPassword = input.type === "password";
  input.type = isPassword ? "text" : "password";
  btn.classList.toggle("is-active", isPassword);
});

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide.bind(this);
    });

    this.querySelector('#modal-overlay').addEventListener('click', this.hide.bind(this));

  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.classList.add('open');
    if (popup) popup.loadContent();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.classList.remove('open');
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class TFSwiper extends HTMLElement {
  constructor() {
    super();
    this.el = null;
    this._swiper = null;
    this._mounted = false;
    this._timer = null;
    this._observer = null;
    this._resizeObserver = null;
    this._lastSignature = "";
    this._isBooting = false;
    this._retryCount = 0;
    this._maxRetry = 20;

    this._onResize = this._onResize.bind(this);
    this._onEditorEvent = this._onEditorEvent.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this._bindElements();
    this._watch();
    this._boot(true);

    window.addEventListener("resize", this._onResize, { passive: true });
    document.addEventListener("shopify:section:load", this._onEditorEvent);
    document.addEventListener("shopify:section:reorder", this._onEditorEvent);
    document.addEventListener("shopify:section:select", this._onEditorEvent);
    document.addEventListener("shopify:block:select", this._onBlockSelect);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
    document.removeEventListener("shopify:section:load", this._onEditorEvent);
    document.removeEventListener("shopify:section:reorder", this._onEditorEvent);
    document.removeEventListener("shopify:section:select", this._onEditorEvent);
    document.removeEventListener("shopify:block:select", this._onBlockSelect);

    this._clearTimer();

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    this._destroy();
    this._mounted = false;
  }

  _bindElements() {
    this.el = this.querySelector(".swiper.tf-swiper");
  }

  _clearTimer() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
  }

  _destroy() {
    const sw = this.el?.swiper || this._swiper;
    if (sw) {
      try {
        sw.destroy(true, true);
      } catch (e) { }
    }
    this._swiper = null;
  }

  _onResize() {
    this._scheduleBoot(false, 80);
  }

  _onEditorEvent(e) {
    if (!e?.target) return;
    if (!e.target.contains(this) && !this.contains(e.target)) return;
    this._scheduleBoot(true, 80);
  }

  _onBlockSelect(e) {
    if (!e?.target) return;
    if (!e.target.contains(this) && !this.contains(e.target)) return;

    this._scheduleBoot(false, 50);

    setTimeout(() => {
      this._slideToSelectedBlock(e.target);
    }, 120);
  }

  _slideToSelectedBlock(target) {
    this._bindElements();
    if (!this.el) return;

    const swiper = this.el.swiper || this._swiper;
    if (!swiper) return;

    const slide = target.closest(".swiper-slide");
    if (!slide || !this.el.contains(slide)) return;

    const realIndexAttr = slide.getAttribute("data-swiper-slide-index");
    const realIndex = realIndexAttr !== null ? parseInt(realIndexAttr, 10) : -1;

    if (swiper.params.loop && realIndex >= 0 && typeof swiper.slideToLoop === "function") {
      try {
        swiper.slideToLoop(realIndex, 300, false);
        this._hardUpdate(swiper);
        return;
      } catch (e) { }
    }

    const slides = Array.from(swiper.slides || []);
    let index = slides.indexOf(slide);

    if (index < 0) {
      const domSlides = Array.from(this.el.querySelectorAll(".swiper-slide"));
      index = domSlides.indexOf(slide);
    }

    if (index >= 0) {
      try {
        swiper.slideTo(index, 300, false);
      } catch (e) { }
      this._hardUpdate(swiper);
    }
  }

  _watch() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    const wrapper = this.querySelector(".swiper-wrapper");
    if (wrapper) {
      this._observer = new MutationObserver((mutations) => {
        let shouldReinit = false;

        for (const mutation of mutations) {
          if (mutation.type === "childList") {
            shouldReinit = true;
            break;
          }
        }

        if (shouldReinit) {
          this._scheduleBoot(true, 80);
        }
      });

      this._observer.observe(wrapper, {
        childList: true
      });
    }

    if (window.ResizeObserver) {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }

      this._resizeObserver = new ResizeObserver(() => {
        this._scheduleBoot(false, 80);
      });

      this._resizeObserver.observe(this);
    }
  }

  _scheduleBoot(force = false, delay = 60) {
    this._clearTimer();
    this._timer = setTimeout(() => this._boot(force), delay);
  }

  num(v, fallback) {
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : fallback;
  }

  _readConfig() {
    const d = this.el?.dataset || {};

    const desktop = this.num(d.preview, 1);
    const desktopXl = d.previewXl !== undefined ? this.num(d.previewXl, desktop) : desktop;
    const tablet = this.num(d.tablet, 1);
    const mobile = this.num(d.mobile, 1);
    const mobileSm = d.mobileSm !== undefined ? this.num(d.mobileSm, mobile) : mobile;

    const spaceLg = this.num(d.spaceLg, 0);
    const spaceXl = d.spaceXl !== undefined ? this.num(d.spaceXl, spaceLg) : spaceLg;
    const spaceMd = this.num(d.spaceMd, 0);
    const space = this.num(d.space, 0);

    const perGroup = this.num(d.pagination, 1);
    const perGroupMd = this.num(d.paginationMd, 1);
    const perGroupLg = this.num(d.paginationLg, 1);
    const perGroupXl = d.paginationXl !== undefined ? this.num(d.paginationXl, perGroupLg) : perGroupLg;

    const gridRows = this.num(d.grid, 1);
    const effect = d.effect || "slide";
    const autoplayEnabled = d.auto === "true" || d.auto === "1";
    const speed = this.num(d.speed, 800);
    const delay = this.num(d.delay, 3000);
    const loop = d.loop === "true" || d.loop === "1";
    const direction = d.direction || "horizontal";

    const paginationEl =
      this.querySelector(".swiper-pagination") ||
      this.el?.querySelector(".swiper-pagination");

    const nextEl =
      this.querySelector(".next-swiper") ||
      this.el?.querySelector(".next-swiper");

    const prevEl =
      this.querySelector(".prev-swiper") ||
      this.el?.querySelector(".prev-swiper");

    const opts = {
      direction,
      speed,
      effect,
      loop,
      observer: false,
      observeParents: false,
      watchOverflow: true,
      slidesPerView: mobile,
      slidesPerGroup: perGroup,
      spaceBetween: space,
      grid: gridRows > 1 ? { rows: gridRows, fill: "row" } : undefined,
      autoplay: autoplayEnabled
        ? { delay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        576: {
          slidesPerView: mobileSm,
          slidesPerGroup: perGroup,
          spaceBetween: space
        },
        768: {
          slidesPerView: tablet,
          slidesPerGroup: perGroupMd,
          spaceBetween: spaceMd
        },
        1150: {
          slidesPerView: desktop,
          slidesPerGroup: perGroupLg,
          spaceBetween: spaceLg
        },
        1440: {
          slidesPerView: desktopXl,
          slidesPerGroup: perGroupXl,
          spaceBetween: spaceXl
        }
      }
    };

    if (effect === "fade") {
      opts.fadeEffect = { crossFade: true };
    }

    if (paginationEl) {
      opts.pagination = {
        el: paginationEl,
        clickable: true
      };
    }

    if (nextEl && prevEl) {
      opts.navigation = {
        nextEl,
        prevEl
      };
    }

    return opts;
  }

  _getSignature() {
    if (!this.el) return "";

    const d = this.el.dataset || {};

    return JSON.stringify({
      preview: d.preview,
      previewXl: d.previewXl,
      tablet: d.tablet,
      mobile: d.mobile,
      mobileSm: d.mobileSm,
      spaceLg: d.spaceLg,
      spaceXl: d.spaceXl,
      spaceMd: d.spaceMd,
      space: d.space,
      pagination: d.pagination,
      paginationMd: d.paginationMd,
      paginationLg: d.paginationLg,
      paginationXl: d.paginationXl,
      grid: d.grid,
      effect: d.effect,
      auto: d.auto,
      speed: d.speed,
      delay: d.delay,
      loop: d.loop,
      direction: d.direction
    });
  }

  _boot(force = false) {
    if (this._isBooting) return;
    this._isBooting = true;

    this._bindElements();

    if (!this.el || !window.Swiper) {
      this._isBooting = false;
      if (this._retryCount < this._maxRetry) {
        this._retryCount += 1;
        this._scheduleBoot(force, 120);
      }
      return;
    }

    const width = this.el.offsetWidth || this.offsetWidth || 0;
    if (width === 0) {
      this._isBooting = false;
      if (this._retryCount < this._maxRetry) {
        this._retryCount += 1;
        this._scheduleBoot(force, 120);
      }
      return;
    }

    this._retryCount = 0;

    const newSignature = this._getSignature();
    const existingSwiper = this.el.swiper || this._swiper;
    const needInit = !existingSwiper;
    const needReinit = force || newSignature !== this._lastSignature;

    if (needInit || needReinit) {
      this._destroy();

      const opts = this._readConfig();
      this._swiper = new window.Swiper(this.el, opts);
      this._lastSignature = newSignature;

      this._afterInit();
      this._isBooting = false;
      return;
    }

    this._hardUpdate(existingSwiper);
    this._isBooting = false;
  }

  _afterInit() {
    const sw = this.el.swiper || this._swiper;
    if (!sw) return;

    this._hardUpdate(sw);

    requestAnimationFrame(() => this._hardUpdate(sw));
    setTimeout(() => this._hardUpdate(sw), 100);
    setTimeout(() => this._hardUpdate(sw), 300);
  }

  _hardUpdate(sw) {
    if (!sw) return;

    try { sw.updateSize(); } catch (e) { }
    try { sw.updateSlides(); } catch (e) { }
    try { sw.updateProgress(); } catch (e) { }
    try { sw.updateSlidesClasses(); } catch (e) { }
    try { sw.pagination?.render?.(); } catch (e) { }
    try { sw.pagination?.update?.(); } catch (e) { }
    try { sw.navigation?.update?.(); } catch (e) { }
    try { sw.update(); } catch (e) { }
  }
}

if (!customElements.get("tf-swiper")) {
  customElements.define("tf-swiper", TFSwiper);
}

class SaleMarquee extends HTMLElement {
  static get observedAttributes() {
    return ['data-speed'];
  }

  constructor() {
    super();

    this.resizeFrame = null;
    this.hasPreparedMarkup = false;

    this.handleResize = this.scheduleRefresh.bind(this);
    this.handleMotionChange = this.scheduleRefresh.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.track = this.querySelector('[data-marquee-track]');
    this.group = this.querySelector('[data-marquee-group]');

    if (!this.viewport || !this.track || !this.group) return;

    if (!this.hasPreparedMarkup) {
      this.originalContent = this.group.innerHTML;
      this.hasPreparedMarkup = true;
    }

    this.reducedMotionMedia = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    this.disconnectObservers();
    this.setupResizeObserver();
    this.setupIntersectionObserver();
    this.setupMotionListener();

    this.scheduleRefresh();

    document.fonts?.ready.then(() => {
      if (this.isConnected) {
        this.scheduleRefresh();
      }
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.resizeFrame);
    this.disconnectObservers();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === 'data-speed' &&
      oldValue !== newValue &&
      this.isConnected
    ) {
      this.scheduleRefresh();
    }
  }

  disconnectObservers() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();

    window.removeEventListener('resize', this.handleResize);

    if (this.reducedMotionMedia?.removeEventListener) {
      this.reducedMotionMedia.removeEventListener(
        'change',
        this.handleMotionChange
      );
    } else {
      this.reducedMotionMedia?.removeListener?.(
        this.handleMotionChange
      );
    }
  }

  setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleRefresh();
      });

      this.resizeObserver.observe(this.viewport);
      return;
    }

    window.addEventListener('resize', this.handleResize, {
      passive: true
    });
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle(
          'is-marquee-paused',
          !entry.isIntersecting
        );
      },
      {
        rootMargin: '100px 0px'
      }
    );

    this.intersectionObserver.observe(this);
  }

  setupMotionListener() {
    if (!this.reducedMotionMedia) return;

    if (this.reducedMotionMedia.addEventListener) {
      this.reducedMotionMedia.addEventListener(
        'change',
        this.handleMotionChange
      );
    } else {
      this.reducedMotionMedia.addListener?.(
        this.handleMotionChange
      );
    }
  }

  scheduleRefresh() {
    if (!this.isConnected) return;

    cancelAnimationFrame(this.resizeFrame);

    this.resizeFrame = requestAnimationFrame(() => {
      this.refresh();
    });
  }

  refresh() {
    if (
      !this.viewport ||
      !this.track ||
      !this.group ||
      !this.viewport.clientWidth
    ) {
      return;
    }

    this.removeAttribute('data-marquee-ready');

    this.track
      .querySelectorAll('[data-marquee-clone]')
      .forEach((clone) => clone.remove());

    this.group.innerHTML = this.originalContent;

    if (this.reducedMotionMedia?.matches) {
      this.style.removeProperty('--sale-marquee-distance');
      this.style.removeProperty('--sale-marquee-duration');
      this.setAttribute('data-marquee-ready', '');
      return;
    }

    this.fillAvailableWidth();

    const groupWidth = this.group.getBoundingClientRect().width;

    if (!groupWidth) return;

    const clone = this.group.cloneNode(true);

    clone.removeAttribute('data-marquee-group');
    clone.setAttribute('data-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    this.track.appendChild(clone);

    const speed = this.getSpeed();
    const duration = Math.max(groupWidth / speed, 4);

    this.style.setProperty(
      '--sale-marquee-distance',
      `${groupWidth}px`
    );

    this.style.setProperty(
      '--sale-marquee-duration',
      `${duration}s`
    );

    this.setAttribute('data-marquee-ready', '');
  }

  fillAvailableWidth() {
    const viewportWidth = this.viewport.clientWidth;
    const maximumIterations = 30;
    let iteration = 0;

    while (
      this.group.getBoundingClientRect().width < viewportWidth &&
      iteration < maximumIterations
    ) {
      this.group.insertAdjacentHTML(
        'beforeend',
        this.originalContent
      );

      iteration += 1;
    }
  }

  getSpeed() {
    const speed = Number.parseFloat(this.dataset.speed);

    return Number.isFinite(speed) && speed > 0
      ? speed
      : 55;
  }
}

if (!customElements.get('sale-marquee')) {
  customElements.define('sale-marquee', SaleMarquee);
}



class TabsComponent extends HTMLElement {
  constructor() {
    super();

    this.tabButtons = [];
    this.tabPanels = [];
    this.selectedIndex = 0;

    this.onTabClick = this.onTabClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  connectedCallback() {
    this.tabButtons = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabPanels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabButtons.length || !this.tabPanels.length) return;

    this.initTabs();

    const selectedIndex = Number(this.dataset.selected || 0);
    this.setActiveTab(Number.isFinite(selectedIndex) ? selectedIndex : 0, false);

    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.tabButtons.forEach((button) => {
      button.removeEventListener('click', this.onTabClick);
    });

    const tabList = this.querySelector('[role="tablist"]');

    if (tabList) {
      tabList.removeEventListener('keydown', this.onKeyDown);
    }

    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  initTabs() {
    this.tabButtons.forEach((button, index) => {
      button.dataset.index = index;

      if (!button.id) {
        button.id = `Tab-${Date.now()}-${index}`;
      }

      const panel = this.tabPanels[index];

      if (panel) {
        if (!panel.id) {
          panel.id = `TabPanel-${Date.now()}-${index}`;
        }

        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', button.id);
      }

      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');

      button.removeEventListener('click', this.onTabClick);
      button.addEventListener('click', this.onTabClick);
    });

    this.tabPanels.forEach((panel, index) => {
      panel.hidden = index !== 0;
      panel.setAttribute('tabindex', '0');
    });

    const tabList = this.querySelector('[role="tablist"]');

    if (tabList) {
      tabList.removeEventListener('keydown', this.onKeyDown);
      tabList.addEventListener('keydown', this.onKeyDown);
    }
  }

  onTabClick(event) {
    event.preventDefault();

    const index = Number(event.currentTarget.dataset.index || 0);

    this.setActiveTab(index, true);
  }

  onKeyDown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (!keys.includes(event.key)) return;

    event.preventDefault();

    let index = this.selectedIndex;

    if (event.key === 'ArrowRight') {
      index = index + 1 >= this.tabButtons.length ? 0 : index + 1;
    }

    if (event.key === 'ArrowLeft') {
      index = index - 1 < 0 ? this.tabButtons.length - 1 : index - 1;
    }

    if (event.key === 'Home') {
      index = 0;
    }

    if (event.key === 'End') {
      index = this.tabButtons.length - 1;
    }

    this.setActiveTab(index, true);
    this.tabButtons[index]?.focus();
  }

  setActiveTab(index, dispatchEvent = true) {
    const button = this.tabButtons[index];
    const panel = this.tabPanels[index];

    if (!button || !panel) return;

    this.tabButtons.forEach((tabButton, tabIndex) => {
      const isActive = tabIndex === index;

      tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
      tabButton.classList.toggle('is-active', isActive);
    });

    this.tabPanels.forEach((tabPanel, panelIndex) => {
      const isActive = panelIndex === index;

      tabPanel.hidden = !isActive;
      tabPanel.classList.toggle('is-active', isActive);
      tabPanel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.selectedIndex = index;
    this.dataset.selected = index;

    this.onTabChange(panel, index);

    if (dispatchEvent) {
      this.dispatchEvent(
        new CustomEvent('tabChange', {
          bubbles: true,
          detail: {
            selectedIndex: index,
            selectedTab: panel,
          },
        })
      );
    }
  }

  onTabChange(panel, index) {}

  onBlockSelect(event) {
    if (!event?.target) return;
    if (!event.target.contains(this) && !this.contains(event.target)) return;

    const blockId =
      event.detail?.blockId ||
      event.target.closest('[data-block-id]')?.dataset.blockId;

    if (!blockId) return;

    const index = this.tabPanels.findIndex((panel) => {
      return panel.dataset.blockId === blockId;
    });

    if (index < 0) return;

    this.setActiveTab(index, true);
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}

class ProductTabs extends TabsComponent {
  onTabChange(panel) {
    this.loadPanelContent(panel);

    requestAnimationFrame(() => {
      this.refreshPanel(panel);
    });

    setTimeout(() => {
      this.refreshPanel(panel);
    }, 120);
  }

  loadPanelContent(panel) {
    if (!panel || panel.dataset.loaded === 'true') return;

    const template = panel.querySelector('template');

    if (!template) {
      panel.dataset.loaded = 'true';
      return;
    }

    panel.appendChild(template.content.cloneNode(true));
    panel.dataset.loaded = 'true';
  }

  refreshPanel(panel) {
    if (!panel) return;

    panel.querySelectorAll('tf-swiper').forEach((swiperComponent) => {
      if (typeof swiperComponent._boot === 'function') {
        try {
          swiperComponent._boot(true);
        } catch (e) {}
      }
    });

    panel.querySelectorAll('.swiper').forEach((swiperEl) => {
      const swiper = swiperEl.swiper;

      if (!swiper) return;

      try { swiper.updateSize(); } catch (e) {}
      try { swiper.updateSlides(); } catch (e) {}
      try { swiper.updateProgress(); } catch (e) {}
      try { swiper.updateSlidesClasses(); } catch (e) {}
      try { swiper.pagination?.render?.(); } catch (e) {}
      try { swiper.pagination?.update?.(); } catch (e) {}
      try { swiper.navigation?.update?.(); } catch (e) {}
      try { swiper.update(); } catch (e) {}
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}

(() => {
  if (window.AmerceProductCardSwatchImage) return;

  window.AmerceProductCardSwatchImage = true;

  const cardSelector = '[data-product-card]';
  const swatchSelector = '[data-product-card-swatch]';

  function getCard(element) {
    return element.closest(cardSelector);
  }

  function getPrimaryImage(card) {
    const image = card?.querySelector(
      '.product-img .img-product',
    );

    return image instanceof HTMLImageElement
      ? image
      : null;
  }

  function storeDefaultImage(card, image) {
    if (card.dataset.defaultImageStored === 'true') {
      return;
    }

    card.dataset.defaultImageStored = 'true';

    card.defaultProductImage = {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || '',
      alt: image.getAttribute('alt') || '',
    };
  }

  function setImage(card, swatch = null) {
    const image = getPrimaryImage(card);

    if (!image) return;

    storeDefaultImage(card, image);

    const defaultImage =
      card.defaultProductImage || {};

    const imageUrl =
      swatch?.dataset.cardImage ||
      defaultImage.src ||
      '';

    const imageAlt =
      swatch?.dataset.cardImageAlt ||
      defaultImage.alt ||
      '';

    if (!imageUrl) return;

    if (swatch) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    } else {
      if (defaultImage.srcset) {
        image.setAttribute(
          'srcset',
          defaultImage.srcset,
        );
      } else {
        image.removeAttribute('srcset');
      }

      if (defaultImage.sizes) {
        image.setAttribute(
          'sizes',
          defaultImage.sizes,
        );
      } else {
        image.removeAttribute('sizes');
      }
    }

    image.src = imageUrl;
    image.alt = imageAlt;

    card.classList.toggle(
      'is-swatch-image-active',
      Boolean(swatch),
    );
  }

  function getSelectedSwatch(card) {
    return (
      card.querySelector(
        `${swatchSelector}[aria-current="true"]`,
      ) ||
      card.querySelector(
        `.product-color-item.active ${swatchSelector}`,
      )
    );
  }

  function previewSwatch(swatch) {
    const card = getCard(swatch);

    if (!card) return;

    setImage(card, swatch);
  }

  function restoreSelectedImage(card) {
    if (!card) return;

    const selectedSwatch =
      getSelectedSwatch(card);

    setImage(card, selectedSwatch);
  }

  function selectSwatch(swatch) {
    const card = getCard(swatch);

    if (!card) return;

    card
      .querySelectorAll(swatchSelector)
      .forEach((item) => {
        const isActive = item === swatch;

        item.toggleAttribute(
          'aria-current',
          isActive,
        );

        item
          .closest('.product-color-item')
          ?.classList.toggle(
            'active',
            isActive,
          );
      });

    setImage(card, swatch);
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (event.pointerType === 'touch') {
        return;
      }

      const swatch = event.target.closest(
        swatchSelector,
      );

      if (!swatch) return;

      if (
        event.relatedTarget instanceof Node &&
        swatch.contains(event.relatedTarget)
      ) {
        return;
      }

      previewSwatch(swatch);
    },
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (event.pointerType === 'touch') {
        return;
      }

      const swatch = event.target.closest(
        swatchSelector,
      );

      if (!swatch) return;

      if (
        event.relatedTarget instanceof Node &&
        swatch.contains(event.relatedTarget)
      ) {
        return;
      }

      restoreSelectedImage(getCard(swatch));
    },
  );

  document.addEventListener(
    'focusin',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const swatch = event.target.closest(
        swatchSelector,
      );

      if (swatch) {
        previewSwatch(swatch);
      }
    },
  );

  document.addEventListener(
    'focusout',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const swatch = event.target.closest(
        swatchSelector,
      );

      if (!swatch) return;

      restoreSelectedImage(getCard(swatch));
    },
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const swatch = event.target.closest(
        swatchSelector,
      );

      if (!swatch) return;

      event.preventDefault();

      selectSwatch(swatch);
    },
  );
})();

(function () {
    const SELECTOR = '[data-tf-animation]';
    const IMAGE_WAIT_TIMEOUT = 1500;

    const prefersReducedanimation = () => {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    };

    function getMobileBreakpoint(element) {
        return parseInt(element.dataset.animationMobileBreakpoint || 767, 10);
    }

    function isMobileAnimationDisabled(element) {
        const disabled = element.dataset.animationDisableMobile === 'true' || element.dataset.animationDisableMobile === '1';

        if (!disabled) return false;

        const breakpoint = getMobileBreakpoint(element);

        return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
    }

    function getAnimationType(element) {
        return element.dataset.animation || 'fade-up';
    }

    function getAnimationDelay(element) {
        return parseInt(element.dataset.animationDelay || 0, 10);
    }

    function getAnimationDuration(element) {
        return parseInt(element.dataset.animationDuration || 600, 10);
    }

    function getAnimationEasing(element) {
        return element.dataset.animationEasing || 'cubic-bezier(0, 0, 0.3, 1)';
    }

    function getInitialState(element) {
        switch (getAnimationType(element)) {
            case 'fade-in':
                return {
                    opacity: 0.01,
                };

            case 'fade-up':
                return {
                    opacity: 0.01,
                    transform: 'translateY(2.5rem)',
                };

            case 'fade-down':
                return {
                    opacity: 0.01,
                    transform: 'translateY(-2.5rem)',
                };

            case 'fade-left':
                return {
                    opacity: 0.01,
                    transform: 'translateX(2.5rem)',
                };

            case 'fade-right':
                return {
                    opacity: 0.01,
                    transform: 'translateX(-2.5rem)',
                };

            case 'zoom-in':
                return {
                    opacity: 0.01,
                    transform: 'scale(0.85)',
                };

            case 'zoom-in-lg':
                return {
                    opacity: 0.01,
                    transform: 'scale(0)',
                };

            case 'zoom-out':
                return {
                    opacity: 0.01,
                    transform: 'scale(1.2)',
                };

            case 'clip-up':
                return {
                    opacity: 1,
                    clipPath: 'inset(100% 0 0 0)',
                };

            case 'clip-left':
                return {
                    opacity: 1,
                    clipPath: 'inset(0 100% 0 0)',
                };

            default:
                return {
                    opacity: 0.01,
                    transform: 'translateY(2.5rem)',
                };
        }
    }

    function getFinalState(element) {
        switch (getAnimationType(element)) {
            case 'clip-up':
            case 'clip-left':
                return {
                    opacity: 1,
                    clipPath: 'inset(0 0 0 0)',
                };

            case 'fade-in':
                return {
                    opacity: 1,
                };

            default:
                return {
                    opacity: 1,
                    transform: 'translate(0, 0) scale(1)',
                };
        }
    }

    function applyState(element, state) {
        Object.entries(state).forEach(([property, value]) => {
            element.style[property] = value;
        });
    }

    /*
     * getFinalState() never leaves a real transform behind - its only
     * non-identity work happens mid-animation, and the settled value is
     * always 'translate(0, 0) scale(1)' (or no transform key at all for
     * fade-in/clip-*). Visually that's a no-op, but a transform other than
     * 'none' - identity included - makes the element a containing block for
     * any descendant `position: fixed` element (drawers, overlays, modals),
     * silently confining them to this element's box instead of the
     * viewport. Drop it once settled so nothing here leaves that trap
     * behind.
     */
    function clearSettledTransform(element) {
        element.style.removeProperty('transform');
    }

    // A lazy image the browser never renders (zero-size box, or hidden by an
    // ancestor) is never fetched, so it fires neither load nor error. Waiting on
    // one holds the element at its initial opacity for good.
    function isImageWaitable(img) {
        if (img.loading !== 'lazy') return true;

        const rect = img.getBoundingClientRect();

        return rect.width > 0 && rect.height > 0;
    }

    function waitForImages(element) {
        const images = Array.from(element.querySelectorAll('img')).filter(
            (img) => !img.complete && isImageWaitable(img)
        );

        if (!images.length) return Promise.resolve();

        const loaded = Promise.all(
            images.map((img) => {
                return new Promise((resolve) => {
                    img.addEventListener('load', resolve, { once: true });
                    img.addEventListener('error', resolve, { once: true });
                });
            })
        );

        // A stalled image delays the reveal at most this long, never forever.
        return Promise.race([
            loaded,
            new Promise((resolve) => setTimeout(resolve, IMAGE_WAIT_TIMEOUT)),
        ]);
    }

    function prepareAnimation(element) {
        if (element.dataset.tfAnimationPrepared === 'true') return;
        if (element.dataset.tfAnimationAnimated === 'true') return;
        if (element.hasAttribute('data-animation-hold')) return;

        element.dataset.tfAnimationPrepared = 'true';

        if (prefersReducedanimation() || isMobileAnimationDisabled(element)) {
            applyState(element, getFinalState(element));
            clearSettledTransform(element);
            element.style.willChange = '';
            element.classList.add('is-animated');
            element.dataset.tfAnimationAnimated = 'true';
            return;
        }

        element.style.willChange = 'opacity, transform, clip-path';

        applyState(element, getInitialState(element));
    }

    async function runAnimation(element) {
        if (element.dataset.tfAnimationAnimated === 'true') return;
        if (element.dataset.tfAnimationRunning === 'true') return;
        if (element.hasAttribute('data-animation-hold')) return;

        element.dataset.tfAnimationRunning = 'true';

        const finalState = getFinalState(element);
        let animation = null;

        try {
            if (prefersReducedanimation() || isMobileAnimationDisabled(element)) return;

            if (!element.hasAttribute('data-animation-instantly')) {
                await waitForImages(element);
            }

            animation = element.animate([getInitialState(element), finalState], {
                duration: getAnimationDuration(element),
                delay: getAnimationDelay(element),
                easing: getAnimationEasing(element),
                fill: 'forwards',
            });

            await animation.finished.catch(() => {});
        } finally {
            // A finished Web Animation with fill:'forwards' keeps computing
            // its last frame - including the transform - even after it
            // ends. cancel() releases that hold so clearSettledTransform()
            // below is the only thing left deciding the element's transform.
            animation?.cancel();

            // However the block above ends, the element has to come back visible —
            // prepareAnimation already hid it.
            applyState(element, finalState);
            clearSettledTransform(element);

            element.style.willChange = '';
            element.classList.add('is-animated');
            element.dataset.tfAnimationAnimated = 'true';
            element.dataset.tfAnimationRunning = 'false';
        }
    }

    function initTfAnimation(scope = document) {
        const elements = [];

        if (scope.matches?.(SELECTOR)) {
            elements.push(scope);
        }

        scope.querySelectorAll?.(SELECTOR).forEach((element) => {
            elements.push(element);
        });

        elements.forEach((element) => {
            if (element.dataset.tfAnimationObserver === 'true') return;

           prepareAnimation(element);

            if (isMobileAnimationDisabled(element)) {
                return;
            }

            if (element.hasAttribute('data-animation-instantly')) {
                runAnimation(element);
                return;
            }

            element.dataset.tfAnimationObserver = 'true';

            window.tfAnimationObserver.observe(element);
        });
    }

    function refreshTfAnimation(element) {
        if (!element) return;

        element.removeAttribute('data-animation-hold');
        element.dataset.tfAnimationPrepared = 'false';
        element.dataset.tfAnimationAnimated = 'false';
        element.dataset.tfAnimationRunning = 'false';

        element.classList.remove('is-animated');

        prepareAnimation(element);
        runAnimation(element);
    }

    window.tfAnimationObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                runAnimation(entry.target);

                if (entry.target.dataset.animationOnce !== 'false') {
                    window.tfAnimationObserver.unobserve(entry.target);
                }
            });
        },
        {
            root: null,
            rootMargin: '0px 0px -50px 0px',
            threshold: 0.1,
        }
    );

    window.initTfAnimation = initTfAnimation;
    window.refreshTfAnimation = refreshTfAnimation;

    function observeAjaxAnimation() {
        if (window.tfAnimationMutationObserver) return;

        window.tfAnimationMutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;

                    if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) {
                        initTfAnimation(node);
                    }
                });
            });
        });

        window.tfAnimationMutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    function boot() {
        initTfAnimation();
        observeAjaxAnimation();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    document.addEventListener('shopify:section:load', (event) => {
        initTfAnimation(event.target);
    });
})();
